import { useQuery } from '@tanstack/react-query';
import type { DocumentDesign } from '@blogforge/shared';
import { api } from './api.js';

/**
 * The design a document is *built* with — the resolved token cascade plus the
 * content column width — for the canvas to preview with.
 *
 * Served by the API rather than derived here on purpose: the cascade differs per
 * medium (posts layer site design tokens and the site's `fontFamily`; emails
 * deliberately skip the site layer) and only the build engine can keep that
 * answer identical to what `buildPost` / `buildEmail` will do. The editor
 * therefore asks for the resolved result instead of re-deriving one that could
 * drift from the published page.
 *
 * Returns undefined while loading or when the document has no id — callers
 * should pass it straight to EditorCanvas, which falls back to the editor's own
 * colours and metrics when there is nothing to mirror.
 */
export function useDocumentDesign(
  kind: 'post' | 'email',
  id: string | undefined,
): DocumentDesign | undefined {
  const { data } = useQuery({
    queryKey: ['design', kind, id],
    queryFn: () => (kind === 'email' ? api.emails.design(id!) : api.posts.design(id!)),
    enabled: !!id,
    // Design changes come from the inspector, which invalidates the document
    // query; this one shares its lifetime, so a stale palette is not worth
    // refetching on every remount.
    staleTime: 30_000,
  });

  return data;
}
